import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaCalendarPlus,
  FaChalkboardTeacher,
  FaMapMarkerAlt,
  FaUsers,
} from "react-icons/fa";
import "./UpcomingSchedule.css";

const THEME_VARIANTS = [
  { color: "#3b82f6", bg: "#eff6ff", icon: FaUsers },
  { color: "#22c55e", bg: "#ecfdf5", icon: FaChalkboardTeacher },
  { color: "#a855f7", bg: "#f5f3ff", icon: FaCalendarCheck },
  { color: "#f97316", bg: "#fff7ed", icon: FaCalendarAlt },
];

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const isFutureBooking = (booking) => {
  const status = String(booking?.status || "").toLowerCase();
  if (status === "cancelled" || status === "completed") return false;
  return new Date(booking.endTime) >= new Date();
};

const isUpcomingBooking = (booking) => {
  const status = String(booking?.status || "").toLowerCase();
  if (!isFutureBooking(booking)) return false;
  if (status === "pending") return true;
  return ["approved", "confirmed", "pending"].includes(status);
};

const normalizeStatus = (booking) => {
  const status = String(booking?.status || "").toLowerCase();
  const now = new Date();
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);
  if (status === "cancelled") return "cancelled";
  if (status === "pending") return "pending";
  if (status === "completed" || end < now) return "completed";
  if (["approved", "confirmed"].includes(status)) return "upcoming";
  return status || "pending";
};

const getBookingTitle = (booking) =>
  booking.purpose || booking.meetingTitle || booking.title || "Meeting";

const getBookingRoom = (booking) => {
  const room =
    booking.roomName ||
    booking.room?.name ||
    booking.roomInfo?.name ||
    booking.room?.roomName;
  return room || "Meeting Room";
};

const getParticipantCount = (booking) => {
  const attendees = Number(booking.attendees || 0);
  if (attendees > 0) return attendees;
  const selected = Array.isArray(booking.selectedMembers) ? booking.selectedMembers.length : 0;
  const team = Array.isArray(booking.teamInfo?.members) ? booking.teamInfo.members.length : 0;
  const total = selected || team;
  return total > 0 ? total : null;
};

const getBadgeLabel = (booking) => {
  const status = String(booking?.status || "").toLowerCase();
  if (status === "pending") return "Pending";
  return "Upcoming";
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const buildTodayChart = (bookings) => {
  const today = new Date();
  const slots = [
    { label: "9 AM", hour: 9 },
    { label: "12 PM", hour: 12 },
    { label: "3 PM", hour: 15 },
    { label: "6 PM", hour: 18 },
  ];

  const values = slots.map(({ hour }) => {
    const slotEnd = new Date(today);
    slotEnd.setHours(hour, 0, 0, 0);
    return bookings.filter((b) => {
      const start = new Date(b.startTime);
      return isSameDay(start, today) && start <= slotEnd;
    }).length;
  });

  const max = Math.max(...values, 1);
  const width = 220;
  const height = 56;
  const padding = 8;
  const step = (width - padding * 2) / (values.length - 1 || 1);

  const points = values.map((value, index) => {
    const x = padding + index * step;
    const y = height - padding - (value / max) * (height - padding * 2);
    return { x, y, value };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

  return { slots, points, linePath, areaPath, width, height, maxValue: max };
};

const UpcomingSchedule = ({ bookings = [], loading = false, maxItems = 3 }) => {
  const navigate = useNavigate();

  const upcomingItems = useMemo(
    () =>
      bookings
        .filter(isUpcomingBooking)
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .slice(0, maxItems),
    [bookings, maxItems]
  );

  const statusStats = useMemo(() => {
    const counts = { upcoming: 0, completed: 0, cancelled: 0 };
    bookings.forEach((b) => {
      const key = normalizeStatus(b);
      if (key === "upcoming" || key === "pending") counts.upcoming += 1;
      else if (counts[key] !== undefined) counts[key] += 1;
    });
    return counts;
  }, [bookings]);

  const todayMeetingCount = useMemo(() => {
    const today = new Date();
    return bookings.filter((b) => {
      const start = new Date(b.startTime);
      return isSameDay(start, today) && isUpcomingBooking(b);
    }).length;
  }, [bookings]);

  const chart = useMemo(
    () => buildTodayChart(bookings.filter(isUpcomingBooking)),
    [bookings]
  );

  return (
    <article className="upcoming-schedule">
      <div className="upcoming-schedule__head">
        <div className="upcoming-schedule__title-wrap">
          <span className="upcoming-schedule__icon-badge">
            <FaCalendarAlt aria-hidden />
          </span>
          <div>
            <h3 className="upcoming-schedule__title">Upcoming Schedule</h3>
            <p className="upcoming-schedule__subtitle">
              {loading
                ? "Fetching your meetings…"
                : `${upcomingItems.length} meeting${upcomingItems.length === 1 ? "" : "s"} lined up`}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="upcoming-schedule__view-all"
          onClick={() => navigate("/meetings/schedule")}
        >
          View All
        </button>
      </div>

      <div className="upcoming-schedule__body">
        {loading ? (
          <ul className="upcoming-schedule__skeleton" aria-label="Loading schedule">
            {[1, 2, 3].map((n) => (
              <li key={n} className="upcoming-schedule__skeleton-row">
                <span className="upcoming-schedule__skeleton-time" />
                <span className="upcoming-schedule__skeleton-dot" />
                <span className="upcoming-schedule__skeleton-content" />
              </li>
            ))}
          </ul>
        ) : upcomingItems.length === 0 ? (
          <div className="upcoming-schedule__empty">
            <div className="upcoming-schedule__empty-visual" aria-hidden="true">
              <span className="upcoming-schedule__empty-ring" />
              <span className="upcoming-schedule__empty-icon">
                <FaCalendarPlus />
              </span>
            </div>
            <h4>No upcoming meetings</h4>
            <p>Your calendar is clear. Book a room to schedule your next session.</p>
            <button
              type="button"
              className="upcoming-schedule__cta"
              onClick={() => navigate("/meetings/book")}
            >
              Book a room
            </button>
          </div>
        ) : (
          <ul className="upcoming-schedule__list">
            {upcomingItems.map((booking, index) => {
              const theme = THEME_VARIANTS[index % THEME_VARIANTS.length];
              const ThemeIcon = theme.icon;
              const badge = getBadgeLabel(booking);
              const participants = getParticipantCount(booking);

              return (
                <li
                  key={booking._id || booking.id || index}
                  className="upcoming-schedule__item"
                  style={{ "--schedule-accent": theme.color, "--schedule-bg": theme.bg }}
                >
                  <div className="upcoming-schedule__time-col">
                    <strong>{formatTime(booking.startTime)}</strong>
                    <span>{formatTime(booking.endTime)}</span>
                  </div>

                  <div className="upcoming-schedule__track" aria-hidden="true">
                    <span className="upcoming-schedule__dot" />
                    {index < upcomingItems.length - 1 && (
                      <span className="upcoming-schedule__line" />
                    )}
                  </div>

                  <span className="upcoming-schedule__item-icon">
                    <ThemeIcon aria-hidden />
                  </span>

                  <div className="upcoming-schedule__details">
                    <strong>{getBookingTitle(booking)}</strong>
                    <span>
                      <FaMapMarkerAlt aria-hidden />
                      {getBookingRoom(booking)}
                    </span>
                    {participants != null && (
                      <span>
                        <FaUsers aria-hidden />
                        {participants} Participant{participants === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  <span className="upcoming-schedule__badge">{badge}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!loading && (
        <section className="upcoming-schedule__overview" aria-label="Today's overview">
          <div className="upcoming-schedule__overview-copy">
            <strong>Today&apos;s Overview</strong>
            <span>Total {todayMeetingCount || upcomingItems.length} meetings</span>
          </div>

          <div className="upcoming-schedule__chart-wrap">
            <svg
              className="upcoming-schedule__chart"
              viewBox={`0 0 ${chart.width} ${chart.height}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="scheduleChartFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path d={chart.areaPath} fill="url(#scheduleChartFill)" />
              <path
                d={chart.linePath}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {chart.points.map((point) => (
                <circle
                  key={`${point.x}-${point.y}`}
                  cx={point.x}
                  cy={point.y}
                  r="3.5"
                  fill="#fff"
                  stroke="#3b82f6"
                  strokeWidth="2"
                />
              ))}
            </svg>
            <div className="upcoming-schedule__chart-labels">
              {chart.slots.map((slot) => (
                <span key={slot.label}>{slot.label}</span>
              ))}
            </div>
          </div>

          <ul className="upcoming-schedule__overview-legend">
            <li>
              <span className="upcoming-schedule__legend-dot upcoming-schedule__legend-dot--blue" />
              Upcoming
              <em>{statusStats.upcoming}</em>
            </li>
            <li>
              <span className="upcoming-schedule__legend-dot upcoming-schedule__legend-dot--green" />
              Completed
              <em>{statusStats.completed}</em>
            </li>
            <li>
              <span className="upcoming-schedule__legend-dot upcoming-schedule__legend-dot--red" />
              Cancelled
              <em>{statusStats.cancelled}</em>
            </li>
          </ul>
        </section>
      )}
    </article>
  );
};

export default UpcomingSchedule;
